// LIBRERIE/IMPORTS
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// LIBRERIE ESTERNE
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBath, faExpand, faBed, faLocationCrosshairs } from "@fortawesome/free-solid-svg-icons";

import he from "he";

// CONFIGURAZIONI
import { AGENCY_ID } from "../../config/appConfig";

// HOOKS
import { useGetProperties } from "../../hooks/useGetProperties";

// COMPONENTI
import LoadingIndicator from "../Loaders/LoadingIndicator";

// STILI
//import cardCarouselStyle from "../../assets/jsStyle/cardCarouselStyle";

// Interfaccie Tipi
interface Vetrina2Props {
    agencyName: string;
}

const Vetrina2: React.FC<Vetrina2Props> = ({ agencyName }) => {
    const { t } = useTranslation();
    const { properties, loading, error } = useGetProperties(
        AGENCY_ID,
        {
            vetrina: 2,
            limit: 6,
            offset: 0,
            withCount: false,
            filters: {
                contract: [],
                type: [],
                provincia: '',
                comune: [],
                frazione: [],
                minRooms: 0,
                minBathrooms: 0,
                minPrice: 0,
                maxPrice: 0,
                sqmtMin: 0,
                sqmtMax: 0,
                gardenPrivate: false,
                terrace: false,
                swimmingPool: false,
                ref: '',
            },
            orderBy: {
                field: '',
                direction: 'desc',
            },
            status: [],
            portal: 56
        }
    );

    // ---- Gestione Stati Iniziali ----
    if (loading) return <LoadingIndicator />;

    if (error) {
        console.error("Errore caricamento Vetrina:", error);
        return <div className="alert alert-warning">{t('common.loadingError', 'Errore caricamento dati.')}</div>;
    }

    if (!properties || properties.length === 0) {
        return (
            <div className="text-center p-5">{t('propertyList.notFound.title')}</div>
        )
    }

    // ---- Preparazione Variabili Comuni ----
    const fallbackImagePath = "/assets/images/no_image_big.jpg";
    const fallbackAltGlobal = t('property.imageAltFallback', 'Immagine non disponibile');

    return (

        <section id="vetrina2-box" className="space-ptb">
            <div className="container-fluid">
                <h2 className="mb-4" dangerouslySetInnerHTML={{ __html: t('homePage.vetrina.title') as string }} />
            </div>
            <div className="container-fluid">
                <div className="row">
                    {/* Utilizza VetrinaProperty per il tipo e slice/map */}
                    {(properties)?.slice(0, 6).map((immobile, index) => {

                        // --- Preparazione Variabili per il Loop ---
                        const propertyUrl = `/immobile/${immobile.id}`;
                        const locationName = immobile.addressDetails.quartiereName ?? immobile.addressDetails.frazioneName ?? immobile.addressDetails.comuneName ?? t('common.locationUnknown', 'Località sconosciuta');
                        const propertyTitle = `${immobile.tipologyDetails.name} in ${immobile.contractDetails.name} ${locationName == 'Centro' || locationName == 'Semicentro' ? 'in' : 'a'} ${locationName}`;
                        const linkTitle = t('property.detailsTitlePattern', { propertyName: propertyTitle });
                        const imageUrl = immobile.mainImage || fallbackImagePath;
                        const imageAlt = (immobile.descriptions && immobile.descriptions[0]?.text.trim() !== "")
                            ? immobile.descriptions[0]?.text.substring(0, 100)
                            : `${propertyTitle} - ${agencyName}`;
                        const contractKey = `property.contractTypes.${immobile.contractDetails.name}`;
                        const contractName = t(contractKey, { defaultValue: immobile.contractDetails.name });
                        //const vani = immobile.premises ?? 0;
                        const roomCount = immobile.rooms ?? 0;
                        const bathroomCount = immobile.bathrooms ?? 0;

                        // --- Inizio JSX Card ---
                        return (
                            <div className="col-12 col-md-6 col-lg-4 mb-4" key={index}>
                                <Link key={immobile.id} to={propertyUrl} title={linkTitle ?? ''} className="property-item property-card d-block">
                                    {/* Immagine */}
                                    <div className="property-image">
                                        <img
                                            className="img-fluid"
                                            src={imageUrl}
                                            alt={imageAlt}
                                            loading="lazy"
                                            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                                const target = e.currentTarget;
                                                const resolvedFallbackSrc = new URL(fallbackImagePath, window.location.origin).href;
                                                if (target.src !== resolvedFallbackSrc) {
                                                    target.src = fallbackImagePath;
                                                }
                                                target.alt = fallbackAltGlobal;
                                            }}
                                        />
                                        {/* Badge Contratto */}
                                        <div className="property-lable">
                                            <span className="badge badge-md badge-primary">
                                                {contractName}
                                            </span>
                                        </div>
                                        {/* Badge Esclusiva */}
                                        {immobile.exclusive && (
                                            <div className="property-esclusiva-box">
                                                <span className="property-esclusiva">
                                                    {t('property.exclusive')}
                                                </span>
                                            </div>
                                        )}
                                        {/* Prezzo */}
                                        <div className="property-price">
                                            {immobile.priceDetails.reservedNegotiation
                                                ? t('property.priceReserved')
                                                : `€ ${new Intl.NumberFormat('it-IT').format(immobile.priceDetails.price)} ${immobile.priceDetails.negotiable ? t('common.negotiable') : ""}`.trim()
                                            }
                                        </div>
                                    </div>

                                    {/* Dettagli Testuali */}
                                    <div className="property-details">
                                        <div className="property-details-inner">
                                            {/* Titolo */}
                                            <h5 className="property-title d-none">
                                                {immobile.tipologyDetails.name}
                                            </h5>
                                            <h5 className="property-title my-2">{propertyTitle}</h5>
                                            {/* Indirizzo */}
                                            <p className="property-address">
                                                <FontAwesomeIcon icon={faLocationCrosshairs} className="mr-2"/>
                                                {immobile.addressDetails.frazioneName || immobile.addressDetails.quartiereName ?
                                                    `${immobile.addressDetails.frazioneName || immobile.addressDetails.quartiereName}, ${immobile.addressDetails.comuneName} (${immobile.addressDetails.provinciaSigla ?? ""})`
                                                    : immobile.addressDetails.comuneName ?
                                                        `${immobile.addressDetails.comuneName} (${immobile.addressDetails.provinciaSigla ?? ""})`
                                                        : immobile.addressDetails.provinciaName ?? ''
                                                }
                                            </p>
                                            {/* Info Camere/Bagni/Mq */}
                                            <ul className="property-info list-unstyled">
                                                {/* Camere (mostra solo se > 0) */}
                                                {roomCount > 0 && (
                                                    <li className="property-bed" title={`${roomCount} ${t('common.room', { count: roomCount })}`}>
                                                        <i>
                                                            <FontAwesomeIcon icon={faBed} />
                                                        </i>
                                                        <span>{immobile.rooms}</span>
                                                        {' '}
                                                        {immobile.rooms === 1 ? (t('common.room') || "") : (t('common.rooms') || "")}
                                                    </li>
                                                )}
                                                {/* Bagni (mostra solo se > 0) */}
                                                {bathroomCount > 0 && (
                                                    <li className="property-bath" title={`${bathroomCount} ${t('common.bathroom', { count: bathroomCount })}`}>
                                                        <i>
                                                            <FontAwesomeIcon icon={faBath} />
                                                        </i>
                                                        <span>{immobile.bathrooms}</span>
                                                        {' '}
                                                        {immobile.bathrooms === 1 ? (t('common.bathroom') || "") : (t('common.bathrooms') || "")}
                                                    </li>
                                                )}
                                                {/* Mq (mostra solo se > 0) */}
                                                {(immobile.sqmt ?? 0) > 0 && (
                                                    <li className="property-m-sqft" title={`${immobile.sqmt} m²`}>
                                                        <i>
                                                            <FontAwesomeIcon icon={faExpand} />
                                                        </i>
                                                        <span className="small">{immobile.sqmt} </span>m<sup>2</sup>
                                                    </li>
                                                )}
                                            </ul>
                                            <p className="property-desc d-none" dangerouslySetInnerHTML={{ __html: he.decode(immobile.descriptions[0].text.substring(0, 150) + "...") }} />
                                        </div>
                                        {/* Riferimento */}
                                        <ul className="property-listing-actions list-unstyled mb-0">
                                            <li className="property-rif">
                                                {t('property.reference')} - {immobile.ref}
                                            </li>
                                        </ul>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section >
    );
};

export default Vetrina2;